import { Button, Checkbox, Flex, Text } from '@mantine/core';
import { createStatusColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/StatusColumn';
import { createTextColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { StatusBadge } from '@dealstack/frontend/shared/components/StatusBadge/StatusBadge';
import type { DocumentSlotRole, ProgrammeSigningDocumentRow } from './ProgrammeSigningPage.types';
import type { CreateProgrammeSigningColumnsArgs } from './createProgrammeSigningColumns.types';
import {
	getProgrammeSigningStatusDefinitions,
	getProgrammeSigningStatusDisplay,
} from './createProgrammeSigningColumns.utils';

/** Matches the per-depth indent the app's other nested tables use (`ExpandableNameCell`). */
const NESTED_SLOT_INDENT_PX = 16;

interface Props {
	row: ProgrammeSigningDocumentRow;
	slotRole?: DocumentSlotRole;
	tMipManage: CreateProgrammeSigningColumnsArgs['tMipManage'];
}

/**
 * The document name, indented when the row is a further slot on a document already listed
 * above — two identical names otherwise read as a duplicate. The line promises no round: a
 * third slot is not "second", and an unticked document is not being signed at all.
 */
const DocumentNameCell = ({ row, slotRole, tMipManage }: Props) => {
	const isAdditionalSlot = slotRole?.isAdditionalSlot ?? false;

	return (
		<Flex
			direction="column"
			justify="center"
			w="100%"
			miw="0"
			pl={isAdditionalSlot ? NESTED_SLOT_INDENT_PX : 0}
		>
			<Text size="B6Medium" truncate>
				{row.documentName}
			</Text>
			{isAdditionalSlot && (
				<Text size="xs" c="dimmed" truncate>
					{tMipManage('documents_page.programme_signing.additional_slot_hint', {
						defaultValue: 'Another signature on this document',
					})}
				</Text>
			)}
		</Flex>
	);
};

/**
 * The leading select column, present only in selection mode. A witness-requiring document says
 * so inline rather than showing a disabled checkbox to hover over; it is still signable one at
 * a time from its own row. Every other unticked row gets an empty cell.
 */
const createSelectColumn = ({
	selection,
	slotRoles,
	tMipManage,
}: Required<
	Pick<CreateProgrammeSigningColumnsArgs, 'selection' | 'slotRoles' | 'tMipManage'>
>): ExtendedColumnDef<ProgrammeSigningDocumentRow> => ({
	id: 'select',
	header: '',
	enableSorting: false,
	enableGlobalFilter: false,
	cell: ({ row }) => {
		const { documentWorkflowInstanceId, signatureId, canSign, canBulkSign } = row.original;

		// Nothing outstanding on this row (already signed, or another provider) — an empty
		// cell, not a reason, since there is no action being withheld.
		if (!canSign && !canBulkSign) {
			return null;
		}

		// Today the only thing that is signable but not bulk-signable is a witness requirement.
		if (!canBulkSign) {
			return (
				<Text size="xs" c="dimmed">
					{tMipManage('documents_page.programme_signing.witness_row_hint', {
						defaultValue: 'Needs a witness — sign individually',
					})}
				</Text>
			);
		}

		// A further slot on a document already offered above gets no checkbox: a round signs one
		// slot per document and defers the rest, so two ticked rows would promise two signatures
		// and deliver one. The name cell nests and labels the row, in every state of the screen,
		// so nothing needs saying here.
		if (!slotRoles.get(signatureId)?.isBulkSlot) {
			return null;
		}

		const checked = selection.isSelected(documentWorkflowInstanceId);
		return (
			<Checkbox
				checked={checked}
				disabled={!selection.canSelect(documentWorkflowInstanceId)}
				onChange={() => selection.toggle(documentWorkflowInstanceId)}
				// One interpolated key rather than a translated verb glued to the name, so a
				// language that puts the verb last can still order it correctly.
				aria-label={tMipManage('documents_page.programme_signing.select_row_aria_label', {
					documentName: row.original.documentName,
					defaultValue: 'Select {{documentName}}',
				})}
			/>
		);
	},
});

/**
 * Columns for the authorised-signatory portal table: an optional select column (selection mode
 * only), document name, status, and per-row View and Sign. Mirrors the admin
 * `createInvestmentProgrammeDocumentsColumns` and reuses its MIP_MANAGE keys.
 */
export const createProgrammeSigningColumns = ({
	onSignRow,
	onViewRow,
	selection,
	slotRoles,
	tMipManage,
}: CreateProgrammeSigningColumnsArgs): ExtendedColumnDef<ProgrammeSigningDocumentRow>[] => [
	...(selection ? [createSelectColumn({ selection, slotRoles, tMipManage })] : []),
	createTextColumn({
		id: 'documentName',
		headerText: tMipManage('documents_page.columns.document_name.header', {
			defaultValue: 'Document',
		}),
		columnLabel: tMipManage('documents_page.columns.document_name.header', {
			defaultValue: 'Document',
		}),
		accessorKey: 'documentName',
		enableSorting: false,
		cell: ({ row }) => (
			<DocumentNameCell
				row={row.original}
				slotRole={slotRoles.get(row.original.signatureId)}
				tMipManage={tMipManage}
			/>
		),
	}),
	createStatusColumn({
		id: 'status',
		headerText: tMipManage('documents_page.columns.status.header', { defaultValue: 'Status' }),
		columnLabel: tMipManage('documents_page.columns.status.header', {
			defaultValue: 'Status',
		}),
		accessorFn: (row) => row.signatureStatus,
		enableSorting: false,
		statusDefinition: getProgrammeSigningStatusDefinitions(tMipManage),
		cell: ({ row }) => {
			const { label, color } = getProgrammeSigningStatusDisplay(
				row.original.signatureStatus,
				tMipManage
			);
			return <StatusBadge text={label} color={color} />;
		},
	}),
	{
		id: 'action',
		header: '',
		enableSorting: false,
		enableGlobalFilter: false,
		cell: ({ row }) => (
			<Flex gap="xs" justify="flex-end">
				{row.original.fileId ? (
					<Button size="xs" variant="default" onClick={() => onViewRow(row.original)}>
						{tMipManage('documents_page.programme_signing.view_button', {
							defaultValue: 'View',
						})}
					</Button>
				) : null}
				{row.original.canSign ? (
					<Button size="xs" onClick={() => onSignRow(row.original)}>
						{tMipManage('documents_page.signer.sign_button', { defaultValue: 'Sign' })}
					</Button>
				) : null}
			</Flex>
		),
	},
];
